/**
 * 学習履歴取得カスタムフック
 * GET /history API との連携
 */

import { useState, useEffect, useCallback } from "react";


export interface HistoryItem {
  id: string;
  questionId: string;
  questionText: string;
  category: string;
  examType: string;
  selectedAnswer: number;
  isCorrect: boolean;
  answeredAt: string;
}

export interface CategoryStat {
  category: string;
  total: number;
  correct: number;
  /** **0〜1 の比率**（百分率ではない）。表示時に 100 倍すること */
  correctRate: number;
}

export interface HistoryStatistics {
  totalAnswers: number;
  correctAnswers: number;
  /** **0〜1 の比率**（百分率ではない）。Lambda 側が correctAnswers / totalAnswers を返す */
  correctRate: number;
  categoryStats: CategoryStat[];
}

interface HistoryResponse {
  history: HistoryItem[];
  statistics: HistoryStatistics;
  limit: number;
  offset: number;
}

interface UseHistoryResult {
  history: HistoryItem[];
  statistics: HistoryStatistics | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * 学習履歴を取得するフック
 * idToken が必要（認証済みユーザーのみ）
 */
export function useHistory(idToken?: string): UseHistoryResult {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [statistics, setStatistics] = useState<HistoryStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!idToken) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Authorization ヘッダーを手動で付与するため、apiGet を拡張
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_BASE_URL}/history?limit=100`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`API エラー: ${response.status}`);
      }

      const json = await response.json();
      if (json.status === "error") {
        throw new Error(json.message || "履歴の取得に失敗しました");
      }

      const data: HistoryResponse = json.data;
      setHistory(data.history);
      setStatistics(data.statistics);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "履歴の取得に失敗しました";
      setError(message);
      console.error("useHistory エラー:", err);
    } finally {
      setIsLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return {
    history,
    statistics,
    isLoading,
    error,
    refetch: fetchHistory,
  };
}
